import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import {
  AccountSummary,
  AccountV2Service,
} from 'src/app/services/account-v2-service';

/**
 * Registry section — public pitch ("Every registration has a page here")
 * or the member "Your register" variant, per the frozen designs. One
 * component, composition switched by input. Member counts are LIVE from
 * /account/summary. Ownership language follows the cross-cutting rule:
 * "in your collection" / "added by you"; never a legal-ownership claim.
 */
@Component({
  selector: 'pla-home-registry',
  templateUrl: './home-registry.component.html',
  styleUrls: ['./home-registry.component.scss'],
})
export class HomeRegistryComponent {
  @Input() public member = false;
  @Input() public summary: AccountSummary | null = null;

  /** Direct registration entry (feedback 23 Jul): no detour via search. */
  public regInput = '';
  public addState: 'idle' | 'adding' | 'added' | 'failed' = 'idle';
  public addedRegistration = '';

  constructor(
    private router: Router,
    private accountService: AccountV2Service
  ) {}

  private canonical(): string {
    return (this.regInput || '').replace(/\s/g, '').toUpperCase();
  }

  /** Public state: straight to the registration's permanent page. */
  public findPage(): void {
    const reg = this.canonical();
    if (!reg) return;
    this.router.navigate(['/plate-detail', reg]);
  }

  /**
   * Member state: add the registration to the user's register directly.
   * Backend canonicalises and upserts; ownership stays unverified
   * ("Added by you") until the account flow verifies it.
   */
  public addToRegister(): void {
    const reg = this.canonical();
    if (!reg || this.addState === 'adding') return;
    this.addState = 'adding';
    this.accountService.addRegistration({ registration: reg }).subscribe({
      next: () => {
        this.addState = 'added';
        this.addedRegistration = reg;
        this.regInput = '';
      },
      error: () => (this.addState = 'failed'),
    });
  }

  public publicPoints = [
    {
      title: 'One permanent page',
      copy: 'Availability, verified sales, valuation and history. The definitive record.',
    },
    {
      title: 'Claim what you own',
      copy: "Verify ownership and build your registration's record over time.",
    },
    {
      title: 'Track any registration',
      copy: 'Watch its value, and be first to know if it comes to market.',
    },
    {
      title: 'Compare every seller',
      copy: "If it's for sale, every dealer offering it appears side by side.",
    },
  ];

  public get memberPoints(): { title: string; copy: string }[] {
    const s = this.summary;
    const points = [] as { title: string; copy: string }[];
    if (s && s.registrations > 0) {
      points.push({
        title:
          s.registrations === 1
            ? 'One plate in your collection'
            : `${s.registrations} plates in your collection`,
        copy: 'Added by you; each record is maintained for good.',
      });
    } else {
      points.push({
        title: 'Add a registration',
        copy: 'Own one? Bring its record under your name.',
      });
    }
    if (s && s.followedPlates > 0) {
      points.push({
        title:
          s.followedPlates === 1
            ? 'One plate tracked'
            : `${s.followedPlates} plates tracked`,
        copy: "You'll be first to know if any of them come to market.",
      });
    } else {
      points.push({
        title: 'Track any registration',
        copy: 'Watch its value, and be first to know if it comes to market.',
      });
    }
    points.push({
      title: 'One permanent page each',
      copy: 'Availability, verified sales, valuation and history, kept in one place.',
    });
    return points;
  }
}
